import { ElementSource } from "chq";
import { ImplementorTemplateParams } from "components/stories/video-studio/utils/creatomate/setCustomTemplate";
import getText from "components/editor/getText";
import { findAllGenericElements } from "../../genericTemplate/utils/findGenericElementByName";
import { cacheAssetIfNeeded } from "./utils/utils";

interface ElementUpdate {
  id?: string;
  updates: Partial<ElementSource>;
}

const genericImageTemplate = async ({
  preview,
  organization,
  story
}: ImplementorTemplateParams) => {
  const { sourceUrl, name } = await cacheAssetIfNeeded(preview, story);
  const json = preview.getSource();
  const elements = json?.elements;

  const {
    storyElement,
    captionElement,
    creatorFirstNameElement,
    creatorLastnameElement,
    creatorNameElement,
    creatorTitleElement,
    creatorDeparmentElement,
    creatorProfessionElement,
    creatorBusinessElement,
    creatorProfileImageElement,
    creatorLocationElement,
    logoElement,
    promptElement,
    trendElement
  } = findAllGenericElements(elements);

  const elementUpdates: ElementUpdate[] = [
    ...storyElement.map(element => ({
      id: element.id,
      updates: {
        source: sourceUrl,
        name
      }
    })),
    ...logoElement.map(element => ({
      id: element.id,
      updates: {
        source: organization.logo.originalUrl,
        name: `${organization.name}'s logo`
      }
    })),
    ...creatorLocationElement.map(element => ({
      id: element.id,
      updates: {
        text: story?.creator?.location?.name || ""
      }
    })),
    ...promptElement.map(element => ({
      id: element.id,
      updates: {
        text: story?.question?.question || ""
      }
    })),
    ...trendElement.map(element => ({
      id: element.id,
      updates: {
        text: story?.question?.parentStoryQuestion?.question || ""
      }
    })),
    ...captionElement.map(element => ({
      id: element.id,
      updates: {
        text: getText(story?.body || "")
      }
    })),
    ...creatorFirstNameElement.map(element => ({
      id: element.id,
      updates: {
        text: story?.creator?.firstName || story?.creator?.name || ""
      }
    })),
    ...creatorLastnameElement.map(element => ({
      id: element.id,
      updates: {
        text: story?.creator?.lastName || story?.creator?.name || ""
      }
    })),
    ...creatorNameElement.map(element => ({
      id: element.id,
      updates: {
        text: story?.creator?.name || ""
      }
    })),
    ...creatorTitleElement.map(element => ({
      id: element.id,
      updates: {
        text: story?.creator?.title || ""
      }
    })),
    ...creatorDeparmentElement.map(element => ({
      id: element.id,
      updates: {
        text: story?.creator?.departments?.[0]?.name || ""
      }
    })),
    ...creatorProfessionElement.map(element => ({
      id: element.id,
      updates: {
        text: story?.creator?.departments?.[0]?.name || ""
      }
    })),
    ...creatorBusinessElement.map(element => ({
      id: element.id,
      updates: {
        text: story?.creator?.departments?.[0]?.name || ""
      }
    })),
    ...creatorProfileImageElement.map(element => ({
      id: element.id,
      updates: {
        source: story?.creator?.avatar?.thumbUrl || "",
        name: `${story?.creator?.name}'s photo`
      }
    }))
  ];

  const elementMappings = elementUpdates
    .filter((update): update is ElementUpdate & { id: string } => Boolean(update.id))
    .reduce((acc, { id, updates }) => {
      acc[id] = updates;
      return acc;
    }, {} as Record<string, Partial<ElementSource>>);

  await preview.setSource({
    ...json,
    elements: elements.map((element: ElementSource) => ({
      ...element,
      ...elementMappings[element.id]
    }))
  }, false);
};

export default genericImageTemplate;
